/**
 * RelatedTraditionCard — inline strip surfaced on the memorial detail
 * page (and on cemetery detail pages) that recommends the funeral
 * traditions article filtered by the deceased's declared religion.
 *
 * The primary target is the master editorial guide
 * `/articles/indian-funeral-traditions-and-memorial-practices`.
 * If the memorial's `religion` field maps to a section inside that
 * article, we deep-link to the corresponding anchor (`#hindu-funeral-…`,
 * `#christian-funeral-…`, etc.) so the reader lands on the relevant
 * portion of the guide.
 */
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";

// Religion → deep-anchor + friendly label. Everything routes to the
// same master article so we don't have to write duplicate content per
// faith; the section headings inside the article become the anchors.
const RELIGION_TRADITIONS = {
  hindu:      { anchor: "hindu-funeral-traditions",       label: "Hindu",       accent: "#B25D38" },
  christian:  { anchor: "christian-funeral-traditions",   label: "Christian",   accent: "#0369A1" },
  catholic:   { anchor: "christian-funeral-traditions",   label: "Christian",   accent: "#0369A1" },
  orthodox:   { anchor: "christian-funeral-traditions",   label: "Christian",   accent: "#0369A1" },
  protestant: { anchor: "christian-funeral-traditions",   label: "Christian",   accent: "#0369A1" },
  muslim:     { anchor: "muslim-funeral-traditions",      label: "Muslim",      accent: "#059669" },
  islam:      { anchor: "muslim-funeral-traditions",      label: "Muslim",      accent: "#059669" },
  sikh:       { anchor: "sikh-funeral-traditions",        label: "Sikh",        accent: "#EA580C" },
  buddhist:   { anchor: "buddhist-funeral-traditions",    label: "Buddhist",    accent: "#7C3AED" },
  jain:       { anchor: "jain-funeral-traditions",        label: "Jain",        accent: "#B45309" },
  parsi:      { anchor: "zoroastrian-parsi-traditions",   label: "Parsi",       accent: "#7C3AED" },
  zoroastrian: { anchor: "zoroastrian-parsi-traditions",  label: "Parsi",       accent: "#7C3AED" },
};

function normalise(r) {
  if (!r) return null;
  return String(r).trim().toLowerCase().split(/\s|-/)[0];
}

export default function RelatedTraditionCard({ religion, className = "" }) {
  const key = normalise(religion);
  const t = key ? RELIGION_TRADITIONS[key] : null;
  const slug = "indian-funeral-traditions-and-memorial-practices";
  const to = t ? `/articles/${slug}#${t.anchor}` : `/articles/${slug}`;
  const label = t?.label || "Indian";
  const accent = t?.accent || "var(--wofy-primary)";

  return (
    <Link
      to={to}
      data-testid="related-tradition-card"
      className={`block wofy-card p-4 hover:shadow-md transition ${className}`}
      style={{ borderColor: accent }}
    >
      <div className="overline flex items-center gap-1" style={{ color: accent }}>
        <BookOpen className="w-3 h-3" /> LEARN ABOUT TRADITIONS
      </div>
      <div className="mt-1 font-display text-lg leading-tight">
        {label} funeral customs, mourning &amp; remembrance
      </div>
      <p className="mt-1 text-xs opacity-70 leading-relaxed">
        Our editorial guide covers immediate rites, mourning periods, annual remembrance ceremonies, and how families
        keep memories alive across generations.
      </p>
      <div className="mt-2 text-xs font-mono" style={{ color: accent }}>Read the guide →</div>
    </Link>
  );
}
